"use client";

/**
 * windowVariants dla templatu CMS `default`.
 *
 * Każdy template ma OWN dispatch lambda + areaOverrides - analog do
 * `template/{symbol}/layout/*.imp` + `template/{symbol}/container/*.imp`
 * w legacy Imperial (per-template view files; same komponenty React są
 * wspólne - `WMenu`, `WDefault`, `WEmpty` itp. live w
 * `components/{windows,cms/registry}/`).
 *
 * **Precedencja kontenera (po refactor 2026-06-02):**
 *   1. React `areaOverrides[symbol][area]` - jeśli zdefiniowany, COMPLETE
 *      renderer (wrapper hardcoded - najwyższy priorytet).
 *   2. DB `container_symbol` (z `imp_cms_container.symbol`) przekazany jako
 *      `ContainerOverride` z adaptera - override dla `defaults[symbol]` lambda.
 *   3. Hardcoded fallback w `defaults[symbol]` (np. `WEmpty` lub `WDefault`).
 *
 * Każda lambda `defaults` używa wzorca:
 *   `const Container = ContainerOverride ?? <hardcoded WEmpty/WDefault>`
 *
 * Dodawanie wpisu / area variantu: zob. dokumentację w
 * `components/cms/registry/variants.tsx` (header file).
 */

import dynamic from "next/dynamic";
import type {
  WMenuContent,
  WLanguageVersionsContent,
  WDropDownMenuContent,
  WPrezentationContent,
  WReferenceLinkingToUrlContent,
  WPathContent,
  WUserAccountContent,
  WDescriptiveWindowContent,
  WLatestNewsContent,
  WLatestBlogPostsContent,
  WLatestRealizationsContent,
  WClientTestimonialsContent,
  WClientTestimonialsCardsContent,
  WRealizationsTestimonialsContent,
  WFaqContent,
  WBlogPopularPostsContent,
  WSearchContent,
  WShopAdditionalSearchOptionsContent,
  WWindowBannerContent,
  WContactFormContent,
  WNewsletterContent,
  WLogotypeScrollerContent,
  WBoxInformationsContent,
  WCallContent,
  WShopPromotionalProductsContent,
  WShopLatestProductsContent,
  WShopRecomendedProductsContent,
  WShopBestsellerProductsContent,
  WShopNewProductsContent,
} from "@/lib/api/cms-window-content";

/* ─── Window components ────────────────────────────────────────────────── */
import { WMenu } from "@/components/windows/WMenu/WMenu";
import { WDescriptiveWindow } from "@/components/windows/WDescriptiveWindow/WDescriptiveWindow";
import { WDedicatedDescriptiveWindow } from "@/components/windows/WDedicatedDescriptiveWindow/WDedicatedDescriptiveWindow";
import { WLanguageVersions } from "@/components/windows/WLanguageVersions/WLanguageVersions";
import { WDropDownMenu } from "@/components/windows/WDropDownMenu/WDropDownMenu";
import { WUserAccount } from "@/components/windows/WUserAccount/WUserAccount";
import WBoxInformations from "@/components/windows/WBoxInformations/WBoxInformations";
import WReferenceLinkingToUrl from "@/components/windows/WReferenceLinkingToUrl/WReferenceLinkingToUrl";
import { WPath } from "@/components/windows/WPath/WPath";
import { WLatestNews } from "@/components/windows/WLatestNews/WLatestNews";
import { WLatestBlogPosts } from "@/components/windows/WLatestBlogPosts/WLatestBlogPosts";
import { WDedicatedJobOffers, type WDedicatedJobOffersContent } from "@/components/windows/WDedicatedJobOffers/WDedicatedJobOffers";
import { WLatestRealizations } from "@/components/windows/WLatestRealizations/WLatestRealizations";
import { WClientTestimonials } from "@/components/windows/WClientTestimonials/WClientTestimonials";
import { WClientTestimonialsCards } from "@/components/windows/WClientTestimonialsCards/WClientTestimonialsCards";
import { WRealizationsTestimonials } from "@/components/windows/WRealizationsTestimonials/WRealizationsTestimonials";
import { WFaq } from "@/components/windows/WFaq/WFaq";
import { WBlogPopularPosts } from "@/components/windows/WBlogPopularPosts/WBlogPopularPosts";
import { WSearch } from "@/components/windows/WSearch/WSearch";
import { WShopAdditionalSearchOptions } from "@/components/windows/WShopAdditionalSearchOptions/WShopAdditionalSearchOptions";
import { WWindowBanner } from "@/components/windows/WWindowBanner/WWindowBanner";
import { WContactForm } from "@/components/windows/WContactForm/WContactForm";
import { WNewsletter } from "@/components/windows/WNewsletter/WNewsletter";
import { WCall } from "@/components/windows/WCall/WCall";
import { WShopPromotionalProducts } from "@/components/windows/WShopPromotionalProducts/WShopPromotionalProducts";
import { WShopLatestProducts } from "@/components/windows/WShopLatestProducts/WShopLatestProducts";
import { WShopRecomendedProducts } from "@/components/windows/WShopRecomendedProducts/WShopRecomendedProducts";
import { WShopBestsellerProducts } from "@/components/windows/WShopBestsellerProducts/WShopBestsellerProducts";
import { WShopNewProducts } from "@/components/windows/WShopNewProducts/WShopNewProducts";

/* WPrezentation - dynamic import (Swiper ~50-80 KB JS + forced reflow).
   Bundle splitting: chunk ładuje się lazy, SSR markup zostaje. */
const WPrezentation = dynamic(
  () => import("@/components/windows/WPrezentation/WPrezentation"),
);

/* WLogotypeScroller - dynamic import (Swiper). Bundle splitting jak WPrezentation. */
const WLogotypeScroller = dynamic(
  () => import("@/components/windows/WLogotypeScroller/WLogotypeScroller"),
);

/* ─── Area variants (visual overrides per area) ───────────────────────── */
import WReferenceLinkingToUrlCtaPill from "@/components/windows/WReferenceLinkingToUrl/WReferenceLinkingToUrlCtaPill";
import WReferenceLinkingToUrlHeaderLink from "@/components/windows/WReferenceLinkingToUrl/WReferenceLinkingToUrlHeaderLink";

/* ─── Containers (wspólne, w components/cms/registry/) ────────────────── */
import { WDefault } from "../../WDefault";
import { WEmpty } from "../../WEmpty";

import type { WindowVariants } from "../../types";

export const windowVariants: WindowVariants = {
  /**
   * Default renderer per `componentSymbol`. Każdy lambda dispatch używa
   * `ContainerOverride` z DB (`container_symbol`) lub hardcoded fallback'u.
   *
   * Konwencja: większość okien fallback'uje do `WEmpty` (passthrough).
   * `WDefault` (h3 z ramką) gdy chcemy żeby Imperial Administrator widoczna
   * nazwa instancji (`imp_cms_window.name`) trafiała do UI jako header sekcji.
   */
  defaults: {
    w_menu: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WMenu content={content as WMenuContent} />
        </Container>
      );
    },
    /* w_menu_selected_catalogs - menu z wybranego poddrzewa katalogow (korzen
       wybrany recznie przez admina LUB = biezaca podstrona). Payload identyczny
       z w_menu (WMenuItem[]), wiec reuzywamy renderer WMenu. `expandAll` - okno
       pokazuje pelne kuratorowane poddrzewo (nie progressive disclosure);
       glebokosc tnie backend przez `max_dep_level`. WEmpty passthrough; admin
       moze nadpisac wrapper przez DB `container_symbol`. */
    w_menu_selected_catalogs: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WMenu content={content as WMenuContent} expandAll />
        </Container>
      );
    },
    w_language_versions: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WLanguageVersions content={content as WLanguageVersionsContent} />
        </Container>
      );
    },
    w_drop_down_menu: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WDropDownMenu content={content as WDropDownMenuContent} />
        </Container>
      );
    },
    /* w_box_informations - boks treściowy w głównej strefie. Hardcoded WDefault
       (nazwa instancji jako h3 + ramka card). Override z DB może zmienić wrapper
       (np. admin ustawi 'grey_title' dla wybranej instancji okna). */
    w_box_informations: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WDefault;
      return (
        <Container title={title} background={background}>
          <WBoxInformations content={content as WBoxInformationsContent | null} />
        </Container>
      );
    },
    w_prezentation: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WPrezentation content={content as WPrezentationContent} />
        </Container>
      );
    },
    /* w_descriptive_window - okno opisowe (dziala jak c_descriptive_page).
       Tresc = akapity imp_paragraph przez ParagraphRenderer. WEmpty passthrough
       (akapity same zawieraja naglowki); admin moze nadpisac wrapper przez DB
       container_symbol. */
    w_descriptive_window: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WDescriptiveWindow content={content as WDescriptiveWindowContent} />
        </Container>
      );
    },
    w_dedicated_descriptive_window: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WDedicatedDescriptiveWindow content={content as WDescriptiveWindowContent} />
        </Container>
      );
    },
    w_reference_linking_to_url: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WReferenceLinkingToUrl content={content as WReferenceLinkingToUrlContent} />
        </Container>
      );
    },
    /* w_path - breadcrumbs. Czyta `pageBreadcrumb` z AdminContext (server-side
       built w PublicLayout). `content` zawiera TYLKO config per-instancja okna. */
    w_path: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WPath content={content as WPathContent | null} />
        </Container>
      );
    },
    /* w_user_account - widget login/account w headerze. Powiązany z katalogiem
       c_user_accounts przez `imp_cms_window.id_catalog`. */
    w_user_account: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WUserAccount content={content as WUserAccountContent | null} />
        </Container>
      );
    },
    /* w_search - box wyszukiwarki z autocomplete. Połączony ze stroną wyników
       c_search przez `imp_cms_window.id_catalog` (getApiContent zwraca results_url).
       WEmpty passthrough - box jest samodzielny wizualnie. */
    w_search: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WSearch content={content as WSearchContent} />
        </Container>
      );
    },
    /* w_shop_additional_search_options - panel filtrow sklepu (Etap 3b).
       Sidebar dla podstrony c_shop_category. WDefault container (h3 nazwa +
       ramka) - admin moze nadpisac przez DB container_symbol na WEmpty gdy
       chce headless panel. */
    w_shop_additional_search_options: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WDefault;
      return (
        <Container title={title} background={background}>
          <WShopAdditionalSearchOptions
            content={content as WShopAdditionalSearchOptionsContent | null}
          />
        </Container>
      );
    },
    /* w_latest_news - widget najnowszych aktualności (czyta c_news z katalogu
       źródłowego `imp_cms_window.id_catalog`). Obszar main; WEmpty passthrough -
       artykuły mają własne nagłówki, nie dublujemy nazwą instancji okna. Admin
       może nadpisać wrapper przez DB `container_symbol`. */
    w_latest_news: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WLatestNews content={content as WLatestNewsContent} />
        </Container>
      );
    },
    /* w_latest_blog_posts - widget najnowszych wpisow bloga (czyta c_blog z calej
       domeny+jezyka). Uklad mozaikowy 1+2 (featured + 2 boczne karty). Liczba
       pozycji z config[count_last]. WEmpty passthrough - wpisy maja wlasne
       naglowki, nie dublujemy nazwa instancji okna; admin moze nadpisac wrapper
       przez DB `container_symbol`. Title z `w.name` (nazwa instancji okna). */
    w_latest_blog_posts: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WLatestBlogPosts content={content as WLatestBlogPostsContent | null} title={title} />
        </Container>
      );
    },
    w_dedicated_job_offers: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WDedicatedJobOffers content={content as WDedicatedJobOffersContent | null} />
        </Container>
      );
    },
    /* w_dedicated_latest_realizations - widget najnowszych realizacji (czyta c_dedicated_realizations
       z calej domeny+jezyka). Grid 3 kart identyczny wizualnie z lista CRealizations.
       Frontend filtruje z listy biezacy article_id (przez URL ?article_id=N) zeby
       na widoku realizacji nie pokazac aktualnie ogladanej. Liczba pozycji z
       config[count_last] (default 4 - po filtracji zostaje 3). */
    w_dedicated_latest_realizations: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WLatestRealizations content={content as WLatestRealizationsContent | null} title={title} />
        </Container>
      );
    },
    /* w_dedicated_client_testimonials - carousel referencji klientow. Pobiera LOSOWO N realizacji
       z niepustym `client_about_realization` (ORDER BY RAND()). Swiper z auto-rotate,
       navigation arrows, pagination dots. WEmpty passthrough. */
    w_dedicated_client_testimonials: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WClientTestimonials content={content as WClientTestimonialsContent | null} title={title} />
        </Container>
      );
    },
    /* w_dedicated_client_testimonials_cards - wariant "cards" (grid 3 kart w Swiper zamiast 1 slide na raz).
       Identyczne dane co w_dedicated_client_testimonials + dodatkowo `lead_text` (paragraf pod tytulem).
       Nav arrows w prawym gornym rogu naglowka, pagination dots pod slajdami. WEmpty passthrough. */
    w_dedicated_client_testimonials_cards: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WClientTestimonialsCards content={content as WClientTestimonialsCardsContent | null} title={title} />
        </Container>
      );
    },
    /* w_dedicated_realizations_testimonials - laczone okno: grid 3 kart realizacji (jak w_dedicated_latest_realizations)
       + carousel testimoniali (jak w_dedicated_client_testimonials) pod spodem. Payload zawiera obie listy,
       backend fetchuje z tego samego IMODEL_C_DEDICATED_REALIZATIONS (2 niezalezne query). WEmpty passthrough. */
    w_dedicated_realizations_testimonials: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WRealizationsTestimonials content={content as WRealizationsTestimonialsContent | null} title={title} />
        </Container>
      );
    },
    /* w_dedicated_faq - okno akordeonu FAQ. Pobiera opublikowane pytania z katalogu zrodlowego
       okna (imp_cms_window.id_catalog wybrany w adminie z dropdown "Katalog z ktorego
       pobiera") przez IMODEL_C_FAQ::getByPublic(). WEmpty passthrough - okno samo
       renderuje h2 + eyebrow, nie dublujemy WDefault ramka. */
    w_dedicated_faq: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WFaq content={content as WFaqContent | null} title={title} />
        </Container>
      );
    },
    /* w_blog_popular_posts - widget najpopularniejszych wpisow bloga (sort po
       count_view DESC, czyta c_blog z calej domeny+jezyka). Pionowa lista
       kompaktowych kart - typowo w sidebarze; backend uzywa wspolnej metody
       modelu `IMODEL_C_BLOG::getForApi(..., 'popular')` (ta sama co
       w_latest_blog_posts w trybie 'latest'). WEmpty passthrough; title z
       `w.name` (nazwa instancji okna). */
    w_blog_popular_posts: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WBlogPopularPosts content={content as WBlogPopularPostsContent | null} title={title} />
        </Container>
      );
    },
    /* w_window_banner - okno banerowe (obraz z linkiem lub blok HTML, opcjonalna
       autorotacja po stronie klienta). Obszar `main` na stronie głównej. WEmpty
       passthrough - banner jest samodzielny wizualnie; admin może nadpisać
       wrapper przez DB `container_symbol`. */
    w_window_banner: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WWindowBanner content={content as WWindowBannerContent} />
        </Container>
      );
    },
    /* w_contact_form - okno formularza kontaktowego (uproszczony c_contact_form).
       Obszar `main` (+ aboveCatalog/belowCatalog/sidebary). Hardcoded WDefault -
       nazwa instancji okna jako header sekcji (h3 + ramka card), bo formularz
       sam nie ma tytułu. Admin może nadpisać wrapper przez DB `container_symbol`. */
    w_contact_form: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WDefault;
      return (
        <Container title={title} background={background}>
          <WContactForm content={content as WContactFormContent} />
        </Container>
      );
    },
    /* w_newsletter - okno zapisu do newslettera (scalone z w_newsletter_popup).
       Tryb inline (formularz w obszarze, w tym w stopce) lub popup (modal).
       Hardcoded WEmpty (passthrough) - formularz sam jest kartą (composes form),
       a w trybie popup renderuje WYŁĄCZNIE portal (inline pusty, bez ramki). */
    w_newsletter: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WNewsletter content={content as WNewsletterContent} />
        </Container>
      );
    },
    /* w_logotype_scroller - poziomy pasek przewijanych logotypów (Swiper).
       Logotypy zarządzane w panelu admina; konfiguracja przewijania z config
       okna. WEmpty passthrough - pasek jest samodzielny wizualnie; admin może
       nadpisać wrapper przez DB `container_symbol`. */
    w_logotype_scroller: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WLogotypeScroller content={content as WLogotypeScrollerContent} />
        </Container>
      );
    },
    /* w_call - floating widget "oddzwonimy" (lewy dolny róg + modal). Widget
       sam zarządza pozycjonowaniem (position: fixed) i z-index - kontener z
       layoutu nie wprowadza żadnej ramki, dlatego WEmpty passthrough. */
    w_call: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WCall content={content as WCallContent | null} />
        </Container>
      );
    },
    /* w_shop_promotional_products - okno "produkty w promocji" (lista N kafelkow
       ProductCard). Reuse `<ProductCard />` z ui/ (kontrakt items[] =
       CShopCategoryProduct). Naglowek sekcji z nazwy instancji okna (title) +
       opcjonalny CTA "zobacz wszystkie" gdy admin ustawil config[list_url].
       WEmpty passthrough - okno samo renderuje h2, nie dublujemy WDefault
       ramka. Admin moze nadpisac wrapper przez DB `container_symbol`. */
    w_shop_promotional_products: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WShopPromotionalProducts
            content={content as WShopPromotionalProductsContent | null}
            title={title}
          />
        </Container>
      );
    },
    /* w_shop_latest_products - okno "najnowsze produkty" (lista N kafelkow
       ProductCard, sort po `add_date DESC`). Struktura renderingu identyczna
       z w_shop_promotional_products - roznica wylacznie w warstwie danych
       (backend sortuje po dacie zamiast filtrowac po promocji). WEmpty
       passthrough - okno samo renderuje h2. Admin moze nadpisac wrapper
       przez DB `container_symbol`. */
    w_shop_latest_products: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WShopLatestProducts
            content={content as WShopLatestProductsContent | null}
            title={title}
          />
        </Container>
      );
    },
    /* w_shop_recomended_products - okno "polecane produkty" (lista N kafelkow
       ProductCard, filtr po `recomended=1`). Struktura renderingu identyczna
       z pozostalymi oknami sklepowymi - roznica wylacznie w warstwie danych
       (backend filtruje po fladze `recomended`). WEmpty passthrough - okno
       samo renderuje h2. Admin moze nadpisac wrapper przez DB `container_symbol`. */
    w_shop_recomended_products: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WShopRecomendedProducts
            content={content as WShopRecomendedProductsContent | null}
            title={title}
          />
        </Container>
      );
    },
    /* w_shop_bestseller_products - okno "najczesciej kupowane produkty" (lista
       N kafelkow ProductCard, filtr po `bestseller=1`). Struktura renderingu
       identyczna z pozostalymi oknami sklepowymi - roznica wylacznie w warstwie
       danych (backend filtruje po fladze `bestseller`). WEmpty passthrough -
       okno samo renderuje h2. Admin moze nadpisac wrapper przez DB
       `container_symbol`. */
    w_shop_bestseller_products: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WShopBestsellerProducts
            content={content as WShopBestsellerProductsContent | null}
            title={title}
          />
        </Container>
      );
    },
    /* w_shop_new_products - okno "nowosci w sklepie" (lista N kafelkow
       ProductCard, filtr po `new=1`). Struktura renderingu identyczna z
       pozostalymi oknami sklepowymi. Rozne od w_shop_latest_products - to
       filtruje po fladze administratora, nie po dacie dodania. WEmpty
       passthrough - okno samo renderuje h2. Admin moze nadpisac wrapper przez
       DB `container_symbol`. */
    w_shop_new_products: ({ content, title, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? WEmpty;
      return (
        <Container background={background}>
          <WShopNewProducts
            content={content as WShopNewProductsContent | null}
            title={title}
          />
        </Container>
      );
    },
  },

  /**
   * Override per `(componentSymbol, AreaSymbol)`. Brak entry -> fallback do
   * `defaults`. Variant może być reusowalny - wystarczy 2 entries z różnymi
   * area-keys wskazujące na ten sam komponent.
   *
   * **WAŻNE:** areaOverrides ma najwyższy priorytet - ignoruje `ContainerOverride`
   * z DB. Variant per-area jest COMPLETE renderer z hardcoded wrapperem.
   * Jeśli chcesz żeby DB miało precedencję dla danego (symbol, area), nie
   * dodawaj areaOverride - DB container_symbol zadziała automatycznie przez
   * `defaults[symbol]`.
   */
  areaOverrides: {
    w_reference_linking_to_url: {
      /* Naglowek strony - dwie wariacje wizualne per instancja okna wrzucona
         do headerCol3/4: zielony pill button (Kontakt) + subtle text link
         (Strefa klienta). Dane wejsciowe standardowe (imp_cms_window.name +
         config[list_url]), tylko wizualnie inaczej renderowane. */
      headerCol3: ({ content, background }) => (
        <WEmpty background={background}>
          <WReferenceLinkingToUrlCtaPill
            content={content as WReferenceLinkingToUrlContent | null}
          />
        </WEmpty>
      ),
      headerCol4: ({ content, background }) => (
        <WEmpty background={background}>
          <WReferenceLinkingToUrlHeaderLink
            content={content as WReferenceLinkingToUrlContent | null}
          />
        </WEmpty>
      ),
    },
    /* w_menu w stopce - nazwa instancji okna (imp_cms_window.name) jako
       h3 nagłówek kolumny stopki ("Usługi", "Firma" itp.). WDefault renderuje
       h3 z `title`, potem WMenu jako lista poniżej. W innych obszarach
       (leftSidebar/rightSidebar/aboveCatalog/belowCatalog) default to WEmpty
       (menu bez ramki). */
    w_menu: {
      footerLeft: ({ content, title, background }) => (
        <WDefault title={title} background={background}>
          <WMenu content={content as WMenuContent} />
        </WDefault>
      ),
      footerMiddle: ({ content, title, background }) => (
        <WDefault title={title} background={background}>
          <WMenu content={content as WMenuContent} />
        </WDefault>
      ),
      footerRight: ({ content, title, background }) => (
        <WDefault title={title} background={background}>
          <WMenu content={content as WMenuContent} />
        </WDefault>
      ),
    },
    /* w_menu_selected_catalogs w stopce - analogicznie do w_menu: nazwa
       instancji okna jako h3 nagłówek kolumny. */
    w_menu_selected_catalogs: {
      footerLeft: ({ content, title, background }) => (
        <WDefault title={title} background={background}>
          <WMenu content={content as WMenuContent} expandAll />
        </WDefault>
      ),
      footerMiddle: ({ content, title, background }) => (
        <WDefault title={title} background={background}>
          <WMenu content={content as WMenuContent} expandAll />
        </WDefault>
      ),
      footerRight: ({ content, title, background }) => (
        <WDefault title={title} background={background}>
          <WMenu content={content as WMenuContent} expandAll />
        </WDefault>
      ),
    },
  },
};
